import * as THREE from 'three';
import type { CharacterRig } from './character-rig.js';
import type { WolfRig } from './wolf-model.js';
import type { createGoblinRatModel } from './goblin-rat-model.js';

/**
 * `rider` segments collide with the rig's other body (the mount) but never with each other;
 * `straddle` rider segments start inside the mount, so they only meet the ground.
 */
export interface DeathSegment { bone: THREE.Object3D; end: THREE.Object3D | THREE.Vector3; start?: THREE.Vector3; radius: number; mass: number; sphere?: boolean; rider?: 'body' | 'straddle' }
export interface DeathJoint { parent: THREE.Object3D; child: THREE.Object3D; anchor: THREE.Object3D; kind: 'fixed' | 'hinge' | 'spherical' }
export interface DeathRig { root: THREE.Group; segments: DeathSegment[]; joints: DeathJoint[]; head?: THREE.Object3D }

export function humanoidDeathRig(rig: CharacterRig): DeathRig {
  const p = rig.proportions;
  const segments: DeathSegment[] = [
    { bone: rig.hips, end: rig.joints.get('spine')!, radius: p.pelvisHalfWidth * .75, mass: 9 },
    { bone: rig.torso, end: rig.joints.get('neck')!, radius: p.chestHalfWidth * .7, mass: 12 },
    { bone: rig.head, end: new THREE.Vector3(0, .08 * p.headScale, 0), radius: .105 * p.headScale, mass: 2, sphere: true },
  ];
  const joints: DeathJoint[] = [
    { parent: rig.hips, child: rig.torso, anchor: rig.torso, kind: 'fixed' },
    { parent: rig.torso, child: rig.head, anchor: rig.head, kind: 'fixed' },
  ];
  for (const [limbs, parent, radius, mass] of [
    [[rig.leftArm, rig.rightArm], rig.torso, .055, 2], [[rig.leftLeg, rig.rightLeg], rig.hips, .085, 5],
  ] as const) for (const limb of limbs) {
    segments.push({ bone: limb.upper, end: limb.lower, radius: radius * p.limbThickness, mass },
      { bone: limb.lower, end: limb.hand, radius: radius * .75 * p.limbThickness, mass: mass * .55 });
    joints.push({ parent, child: limb.upper, anchor: limb.upper, kind: 'spherical' },
      { parent: limb.upper, child: limb.lower, anchor: limb.lower, kind: 'hinge' });
    if (parent === rig.hips) {
      segments.push({ bone: limb.hand, end: new THREE.Vector3(0, -.025, .06), radius: .07, mass: .2, sphere: true });
      joints.push({ parent: limb.lower, child: limb.hand, anchor: limb.hand, kind: 'fixed' });
    }
  }
  return { root: rig.root, head: rig.head, segments, joints };
}

export function wolfDeathRig(rig: WolfRig): DeathRig {
  const bone = (name: string) => { const joint = rig.joints.get(name); if (!joint) throw new Error(`Missing wolf death joint: ${name}`); return joint; };
  const body = bone('body'), pelvis = bone('pelvis'), chest = bone('chest'), head = bone('head');
  const segments: DeathSegment[] = [
    { bone: body, end: pelvis, radius: .21, mass: 8 },
    { bone: pelvis, end: new THREE.Vector3(0, 0, -.22), radius: .24, mass: 7 },
    { bone: chest, end: bone('neck'), radius: .26, mass: 10 },
    { bone: head, start: new THREE.Vector3(0, 0, -.08), end: new THREE.Vector3(0, -.06, .46), radius: .13, mass: 2 },
  ];
  const joints: DeathJoint[] = [
    { parent: body, child: pelvis, anchor: pelvis, kind: 'fixed' },
    { parent: body, child: chest, anchor: chest, kind: 'fixed' },
    { parent: chest, child: head, anchor: head, kind: 'fixed' },
  ];
  for (const side of ['L', 'R']) for (const names of [
    [`shoulder_${side}`, `elbow_${side}`, `wrist_${side}`, `front_paw_${side}`],
    [`hip_${side}`, `knee_${side}`, `hock_${side}`, `ankle_${side}`, `hind_paw_${side}`],
  ]) {
    let parent: THREE.Object3D = names[0].startsWith('hip') ? pelvis : chest;
    for (let i = 0; i < names.length - 1; i++) {
      const part = bone(names[i]);
      segments.push({ bone: part, end: bone(names[i + 1]), radius: i ? .045 : .075, mass: i ? .5 : 1.5 });
      joints.push({ parent, child: part, anchor: part, kind: i ? 'hinge' : 'spherical' }); parent = part;
    }
  }
  return { root: rig.root, segments, joints };
}

export function mountedDeathRig(root: THREE.Group, rider: CharacterRig, rat: ReturnType<typeof createGoblinRatModel>): DeathRig {
  const human = humanoidDeathRig(rider);
  const segments: DeathSegment[] = [
    { bone: rat.root, start: new THREE.Vector3(0, .61, -.65), end: new THREE.Vector3(0, .61, .52), radius: .32, mass: 18 },
    { bone: rat.head, end: new THREE.Vector3(0, 0, .4), radius: .15, mass: 2 },
  ];
  const joints: DeathJoint[] = [{ parent: rat.root, child: rat.head, anchor: rat.head, kind: 'fixed' }];
  for (const leg of rat.legs) {
    segments.push({ bone: leg.upper, end: leg.lower, radius: .075, mass: 1.5 }, { bone: leg.lower, end: leg.paw, radius: .055, mass: .6 });
    joints.push({ parent: rat.root, child: leg.upper, anchor: leg.upper, kind: 'spherical' },
      { parent: leg.upper, child: leg.lower, anchor: leg.lower, kind: 'hinge' });
  }
  // The rider is deliberately not joined to the saddle: both fall independently in the same world and land on each other.
  const legs = new Set([rider.leftLeg, rider.rightLeg].flatMap(leg => [leg.upper, leg.lower, leg.hand]));
  const seated = human.segments.map(segment => ({ ...segment, rider: legs.has(segment.bone) ? 'straddle' as const : 'body' as const }));
  return { root, head: rider.head, segments: [...segments, ...seated], joints: [...joints, ...human.joints] };
}
